import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Lock, UserPlus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { InviteFirstAdminDialog } from "@/components/admin/invite-first-admin-dialog"
import { PendingFirstAdminInviteCard } from "@/components/admin/pending-first-admin-invite-card"
import { BrandLiveBadge } from "@/components/brand-live-badge"
import { TierBadge } from "@/components/tier-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { agencySettingsBrandsPath } from "@/routes"
import type { AgencyBrandShowProps } from "@/types/agency"

/**
 * Agency/Brands/Show — single brand detail (OKS-13 BE TRD §API).
 *
 * Three sections per UX Spec:
 *   1. **Profile** — name / slug / locale / created_at / live status.
 *      Read-only on the agency surface; edits live on the brand surface
 *      (OKS-14).
 *   2. **Team** — read-only roster of accepted admin/member users on
 *      this brand (Rule 10 / AC6 — agency cannot mutate brand team
 *      membership). `<TierBadge>` per row.
 *   3. **Pending first-admin invite** — `<PendingFirstAdminInviteCard>`
 *      from OKS-12, reused as-is, rendered with `viewer="agency"`.
 *      Three branches (pending/expired card, orphan empty state, hidden
 *      when admin already accepted) all carry through.
 *
 *      **OKS-922 — the orphan branch offers NO CTA to an agency viewer.**
 *      `can_invite_first_admin` carries an `admin_role?` term, so it is
 *      false for every natural agency user. Resend / Revoke on an
 *      EXISTING invite are unaffected and still work for them — those go
 *      through `InvitationsController`, which is permission-gated rather
 *      than role-gated.
 *
 *      This block previously asserted that the invite policy's agency
 *      branch made the dialog work for agency callers. That was false —
 *      but NOT because the permission is missing. `InvitationPolicy`
 *      genuinely has `create_allowed_for_agency?`, which permits a
 *      brand-admin invite for an actively operated brand. It is simply
 *      never REACHED: the dialog posts to `Admin::InvitationsController`,
 *      whose `ensure_role!` redirects to /dashboard before the action
 *      and before `authorize!`. Wrong door, not a missing permission.
 *      Do not restate this as "agency callers are not permitted" — that
 *      is a different false claim.
 *
 * Locked QA testid (OKS-13 v4 freeze):
 *   - `agency-brand-detail[data-brand-id]` (page region)
 *   - `agency-brand-live-badge[data-state]` (header status chip)
 *   - `agency-brand-team-card` (Team Card region)
 *   - `agency-brand-team-row[data-user-id]` per row
 *   - `agency-tier-badge[data-state]` per Team row
 *   - `admin-pending-first-admin-card` (reused OKS-12 component carries
 *     its admin-prefixed testids by design — single QA contract for the
 *     pending-first-admin Card across both surfaces. The component is
 *     surface-agnostic; the SURFACE difference is carried by the
 *     `viewer` prop and by the server-derived `can_invite_first_admin`,
 *     NOT by the invite endpoints behaving the same for both callers —
 *     they do not, see §3 above.)
 */
export default function AgencyBrandsShow({
  brand,
  team,
  pending_first_admin_invite,
  can_invite_first_admin,
}: AgencyBrandShowProps) {
  const { t, i18n } = useTranslation()

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <AppLayout>
      <Head title={brand.name} />
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="agency-brand-detail"
        data-brand-id={brand.id}
      >
        <header className="flex flex-col gap-2">
          {/* OKS-854 — back returns to the Brands TAB inside Agency
              Settings (UX Spec §1), not the retired top-level list. */}
          <Link
            href={agencySettingsBrandsPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("agency.brand_detail.back_to_brands")}
          </Link>
          {/*
           * `BrandLiveBadge` is a no-op for `live: true` so the silent
           * norm reads cleanly; chip only appears for pending-activation
           * brands. testId override per QA v4 contract.
           */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {brand.name}
            </h1>
            <BrandLiveBadge
              live={brand.live}
              testId="agency-brand-live-badge"
            />
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Profile */}
          <Card data-testid="agency-brand-profile-card">
            <CardHeader>
              <CardTitle>
                {t("agency.brand_detail.profile_card.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <ProfileRow
                label={t("agency.brand_detail.profile_card.slug_label")}
                value={brand.slug}
              />
              <ProfileRow
                label={t("agency.brand_detail.profile_card.locale_label")}
                value={brand.locale.toUpperCase()}
              />
              <ProfileRow
                label={t("agency.brand_detail.profile_card.created_at_label")}
                value={dateFmt.format(new Date(brand.created_at))}
              />
              <ProfileRow
                label={t("agency.brand_detail.profile_card.live_label")}
                value={
                  brand.live
                    ? t("agency.brand_detail.profile_card.live_yes")
                    : t("agency.brand_detail.profile_card.live_no")
                }
              />
            </CardContent>
          </Card>

          {/* Team — read-only on the agency surface (Rule 10 / AC6). */}
          <Card data-testid="agency-brand-team-card">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>{t("agency.brand_detail.team_card.title")}</CardTitle>
              {/*
               * Read-only hint — explains *why* there is no manage
               * affordance on this surface. Lock icon mirrors OKS-12
               * "Locked" hint pattern. Sighted users get the visual cue;
               * SR users get the same content as readable text.
               */}
              <span
                className="text-muted-foreground inline-flex items-center gap-1 text-xs"
                data-testid="agency-brand-team-readonly-hint"
              >
                <Lock className="size-3" aria-hidden="true" />
                {t("agency.brand_detail.team_card.readonly_hint")}
              </span>
            </CardHeader>
            <CardContent>
              {team.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("agency.brand_detail.team_card.empty")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("agency.brand_detail.team_card.column.name")}
                      </TableHead>
                      <TableHead>
                        {t("agency.brand_detail.team_card.column.email")}
                      </TableHead>
                      <TableHead>
                        {t("agency.brand_detail.team_card.column.tier")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.map((member) => (
                      <TableRow
                        key={member.id}
                        data-testid="agency-brand-team-row"
                        data-user-id={member.id}
                      >
                        <TableCell className="font-medium">
                          {member.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {member.email}
                        </TableCell>
                        <TableCell>
                          {/*
                           * `TierBadge` is server-authoritative on
                           * `member.tenant_role`. Null tier (legacy
                           * accounts pre-BE6 partition) renders an
                           * em-dash placeholder instead of a chip — the
                           * row still appears in the team list, but
                           * carries no tier signal until backend
                           * backfills.
                           */}
                          {member.tenant_role ? (
                            <TierBadge
                              tier={member.tenant_role}
                              testId="agency-tier-badge"
                            />
                          ) : (
                            <span
                              className="text-muted-foreground"
                              aria-hidden="true"
                            >
                              —
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/*
         * Pending first-admin invite — OKS-12 component reused verbatim.
         * Three branches (pending/expired card, orphan empty CTA, hidden
         * when admin already accepted) carry through unchanged.
         *
         * OKS-922 — the `inviteAction` slot is now UNREACHABLE on this
         * surface: `can_invite_first_admin` carries an `admin_role?` term,
         * so it is false for every natural agency user. The slot is kept
         * (rather than deleted) because an OKS Admin in "act as agency"
         * still passes that term, and for them the flow genuinely
         * completes.
         *
         * See the file docblock §3 for why the previous justification of
         * this slot was wrong: the permission genuinely exists
         * (`create_allowed_for_agency?`) and is simply never reached,
         * because the role gate redirects before `authorize!` runs.
         * A wrong actor, not a missing permission.
         */}
        <PendingFirstAdminInviteCard
          tenantId={brand.id}
          tenantKind="brand"
          viewer="agency"
          invite={pending_first_admin_invite}
          teamSize={team.length}
          inviteAction={
            can_invite_first_admin ? (
              <InviteFirstAdminDialog
                tenant={{ kind: "brand", id: brand.id, name: brand.name }}
                trigger={
                  <Button
                    type="button"
                    data-testid="admin-pending-first-admin-empty-cta"
                  >
                    <UserPlus className="size-4" aria-hidden="true" />
                    {t("admin.invites.card.empty.cta")}
                  </Button>
                }
              />
            ) : undefined
          }
        />
      </div>
    </AppLayout>
  )
}

interface ProfileRowProps {
  label: string
  value: string
}

/**
 * Profile row — label/value pair. Mirrors `Admin/Brands/Show`'s
 * ProfileRow shape (no Icon variant on the agency surface — the admin
 * shape uses icons for visual scanning of a denser metadata Card; the
 * agency surface has fewer rows so plain label/value reads cleanly).
 */
function ProfileRow({ label, value }: ProfileRowProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground w-32 shrink-0 text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <span className="text-foreground truncate text-sm font-medium">
        {value}
      </span>
    </div>
  )
}
